import {Suspense} from 'react';
import {useNavigate} from 'react-router';
import clsx from 'clsx';
import {CreatedMaintenanceRecordRequest} from '@di-shared/api/di/__generated';
import {emitEventLog} from '@di-shared/features/analytics';
import FocusToScroll from '@di-shared/features/common/FocusToScroll';
import InputText from '@di-shared/features/common/InputText';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import CarInfo, {CarInfoLoading} from '@di-shared/features/diag/exchange/CarInfo';
import {ERR_4090_CONFLICT} from '@di-shared/features/diag/exchange/history/DiagExchangeHistoryDetailEdit';
import SestExchangeRegisterInfoTitle, {
  SestExchangeRegisterInfoTitleLoading,
} from '@di-shared/features/diag/exchange/register/SestExchangeRegisterInfoTitle';
import useSestExchangeRegisterForm from '@di-shared/features/diag/exchange/register/useSestExchangeRegisterForm';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import PreventForm from '@shared/PreventForm';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';

interface SestExchangeRegisterProps {
  sestCodes: CreatedMaintenanceRecordRequest.sestCode[];
  vin: string;
  isAccessDI: boolean;
}

function SestExchangeRegister({sestCodes, vin, isAccessDI}: SestExchangeRegisterProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();

  const {resolveError, getErrorCode} = useApiError();
  const {
    isRegisterButtonDisabled,
    exchangeDate,
    isExchangeDateError,
    isOdometerError,
    enteredOdometer,
    updateEnteredOdometer,
    updateExchangeDate,
    hasBattery,
    hasEngineOil,
    hasBrakePad,
    registerRecord,
  } = useSestExchangeRegisterForm(vin, sestCodes);
  const {openConfirm, backOrClose, openAlert} = useCcWebviewUtilsHelper();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultDistanceSymbol} = useUnitConverter();

  const [handleSubmitMaintenanceRecord] = usePendingHandler(
    async () => {
      await registerRecord();
      if (sestCodes.includes(CreatedMaintenanceRecordRequest.sestCode._18)) {
        await openAlert({title: t('T30330'), message: t('T30332'), okText: t('T10114')});
        emitEventLog({
          event: 'consumables_exchange_update',
          form_step: 'complete',
          click_text: t('T30154'),
          click_location: 'body',
        });
      }
      WebviewUtils.showToast(t('T30335'));
      if (isAccessDI) {
        ccWebviewUtils.closeContent();
      } else {
        navigate(ROUTE_PATHS.HOME_VEHICLE_TAB.getFullPath(vin));
      }
    },
    async (error) => {
      //TODO: 날짜를 다시 확인해주세요 validate

      /**
       * [2025-11-25:Jeyoung Lee]
       * 4090 이미 등록된 레코드에 대한 에러코드
       */
      if (getErrorCode(error) === ERR_4090_CONFLICT) {
        const isConfirm = await openConfirm({
          title: t('T30590'),
          message: t('T30591'),
          okText: t('T30637'),
          cancelText: t('T30593'),
        });
        if (isConfirm) {
          backOrClose();
        }
        return;
      }

      await openAlert({
        title: t('T30331'),
        message: t('T10160_$tel', {tel: import.meta.env.VITE_EMERGENCY_TEL_BRAND}),
        okText: t('T10114'),
      });
      emitEventLog({
        event: 'consumables_exchange_update',
        form_step: 'update failed',
        click_text: t('T30154'),
        click_location: 'body',
      });
      await resolveError(error);
    }
  );

  return (
    <PreventForm onSubmit={handleSubmitMaintenanceRecord}>
      <main
        className="content page_car_management"
        style={{marginTop: 56}}
      >
        <section className="section section_exchange_register">
          <Suspense fallback={<SestExchangeRegisterInfoTitleLoading />}>
            <SestExchangeRegisterInfoTitle sestCodes={sestCodes} />
          </Suspense>
          <Suspense fallback={<CarInfoLoading />}>
            <CarInfo vin={vin} />
          </Suspense>
          <div className="box_outline_group">
            <div
              className={clsx('field_box', {
                input_completed: !!exchangeDate,
                input_error: isExchangeDateError,
              })}
            >
              <label
                htmlFor="exchangeDate"
                className="label"
              >
                {t('T30158')}
              </label>
              <div className="input_box">
                <input
                  id="exchangeDate"
                  className="input input_date"
                  placeholder={t('T30159')}
                  readOnly
                  required
                  value={exchangeDate ? CldrDatetimeFormatter.format(exchangeDate, 'yMMMd')?.toString() : ''}
                  onClick={async () => {
                    const selectedDate = await ccWebviewUtils.openBottomSheetWebViewUrl<Date | null>(
                      ROUTE_PATHS.BOTTOM_SHEET_DIAG_EXCHANGE_REGISTER_DATE_PICKER.getFullPath(),
                      {selectedDate: exchangeDate}
                    );
                    if (selectedDate) {
                      updateExchangeDate(selectedDate);
                    }
                  }}
                  onChange={() => {}}
                />
              </div>
            </div>
            {isExchangeDateError && <p className="txt_error body_small_r">{t('T30160')}</p>}
            <div
              className={clsx('field_box', {
                input_completed: !!enteredOdometer,
                input_error: isOdometerError,
              })}
            >
              <label
                htmlFor="exchangeDistance"
                className="label"
              >
                {t('T30163')}
              </label>
              <div className="input_box">
                <FocusToScroll>
                  <InputText
                    initValue={enteredOdometer ? formatNumberWithCommas(enteredOdometer) : ''}
                    inputMode={'numeric'}
                    onChange={(odometer) => updateEnteredOdometer(odometer)}
                    id="exchangeDistance"
                    className="input input_text"
                    placeholder="0"
                    regex={/[^0-9]/g}
                    required={true}
                  />
                </FocusToScroll>
                <span className="input_unit">{defaultDistanceSymbol}</span>
              </div>
            </div>
            {isOdometerError && <p className="txt_error body_small_r">{t('T30162')}</p>}
          </div>
        </section>

        {(hasEngineOil || hasBattery || hasBrakePad) && <div className="divider divider_thick" />}
        <section className="section section_notice">
          <ul className="notice_list">
            {hasEngineOil && (
              <>
                <li className="notice_item">{t('T30164')}</li>
              </>
            )}
            {hasBattery && (
              <>
                <li className="notice_item">{t('T30327')}</li>
                <li
                  className="notice_item"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('T30328')}
                </li>
              </>
            )}
            {hasBrakePad && (
              <>
                <li className="notice_item">{t('T30329')}</li>
              </>
            )}
          </ul>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="submit"
          className="btn btn_large btn_filled btn_full"
          disabled={isRegisterButtonDisabled}
          onClick={() =>
            emitEventLog({
              event: 'consumables_exchange_update',
              form_step: 'enter consumable exchange details',
              click_text: t('T30165'),
              click_location: 'body',
            })
          }
        >
          {t('T30165')}
        </button>
      </div>
    </PreventForm>
  );
}

export default SestExchangeRegister;

export function SestExchangeRegisterLoading() {
  const {t} = useInferableTranslation();
  return (
    <>
      <main className="content page_car_management">
        <section className="section section_exchange_register">
          <SestExchangeRegisterInfoTitleLoading />
          <CarInfoLoading />
          <div className="box_outline_group">
            <div
              className="field_box skeleton"
              style={{height: 46}}
            />
            <div
              className="field_box skeleton"
              style={{height: 46}}
            />
          </div>
        </section>
        <div className="divider divider_thick"></div>
        <section className="section section_notice">
          <ul className="notice_list">
            <li
              className="notice_item skeleton"
              style={{height: 20}}
            />
          </ul>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="submit"
          className="btn btn_large btn_filled btn_full"
          disabled={true}
        >
          {t('T30165')}
        </button>
      </div>
    </>
  );
}
